import React from 'react';
import { Link } from 'react-router-dom';
import styles from './LandingPage.module.css';

const featureCards = [
    {
        title: 'For coaches',
        description: 'Build training plans, assign sessions fast, and review athlete completion without digging through tables.',
        points: ['Plan library', 'Session builder', 'Completion insights'],
    },
    {
        title: 'For athletes',
        description: 'Find coaches, subscribe to a plan, follow the next training, and log what was completed or failed.',
        points: ['Coach discovery', 'Subscriptions', 'Progress tracking'],
    },
];

const workflowCards = [
    {
        eyebrow: '1. Create',
        title: 'Coaches create reusable plans',
        text: 'Structure a plan once, then keep adding new trainings as athletes progress.',
    },
    {
        eyebrow: '2. Deliver',
        title: 'Athletes subscribe and receive the next session',
        text: 'The next training stays front and center, so the product always answers “what do I do now?”',
    },
    {
        eyebrow: '3. Review',
        title: 'Completion signals flow back to the coach',
        text: 'Mark each exercise as completed or failed and turn that into quick coaching visibility.',
    },
];

const LandingPage = () => {
    return (
        <main className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroCopy}>
                    <span className={styles.kicker}>Training system for coaches and athletes</span>
                    <h1>Helping you achieve perfection.</h1>
                    <div className={styles.missionBlock}>
                        <p className={styles.missionLead}>
                            Built for athletes and coaches who care about precision, progression, and purposeful work.
                        </p>
                        <p>
                            LtW turns training plans, daily sessions, and athlete follow-through into one focused system.
                            Coaches shape the path. Athletes see what is next. Both sides stay connected to the quality of the work.
                        </p>
                    </div>

                    <div className={styles.heroActions}>
                        <Link className={styles.primaryButton} to="/register" state={{ prefilledRole: 'coach' }}>
                            Join as coach
                        </Link>
                        <Link className={styles.secondaryButton} to="/register" state={{ prefilledRole: 'user' }}>
                            Join as athlete
                        </Link>
                        <Link className={styles.secondaryButton} to="/login">
                            Log In
                        </Link>
                    </div>
                </div>

                <div className={styles.heroPreview}>
                    <div className={styles.imagePanel}>
                        <img src="/black_and_white_image.png" alt="Underwater training" className={styles.heroImage} />
                        <div className={styles.imageOverlay}>
                            <span>Lead the Way</span>
                            <strong>Precision for every session.</strong>
                            <p>The system behind better preparation, clearer execution, and stronger feedback loops.</p>
                        </div>
                    </div>

                    <div className={styles.previewGrid}>
                        <article className={styles.previewCard}>
                            <span>Coach view</span>
                            <strong>Create plans, assign trainings, review real completion.</strong>
                        </article>
                        <article className={styles.previewCard}>
                            <span>Athlete view</span>
                            <strong>See the next session, train it, and log what actually happened.</strong>
                        </article>
                    </div>
                </div>
            </section>

            <section className={styles.valueSection}>
                <div className={styles.sectionHeader}>
                    <span className={styles.kicker}>Two-sided product value</span>
                    <h2>One product, two clean experiences.</h2>
                    <p>
                        Logged-out visitors see a focused product story. Logged-in users skip marketing entirely
                        and go straight into a role-specific summary workspace.
                    </p>
                </div>

                <div className={styles.featureGrid}>
                    {featureCards.map((card) => (
                        <article key={card.title} className={styles.featureCard}>
                            <h3>{card.title}</h3>
                            <p>{card.description}</p>
                            <ul>
                                {card.points.map((point) => (
                                    <li key={point}>{point}</li>
                                ))}
                            </ul>
                        </article>
                    ))}
                </div>
            </section>

            <section className={styles.workflowSection}>
                <div className={styles.sectionHeader}>
                    <span className={styles.kicker}>Product flow</span>
                    <h2>Designed around the real training loop.</h2>
                </div>

                <div className={styles.workflowGrid}>
                    {workflowCards.map((card) => (
                        <article key={card.title} className={styles.workflowCard}>
                            <span>{card.eyebrow}</span>
                            <h3>{card.title}</h3>
                            <p>{card.text}</p>
                        </article>
                    ))}
                </div>
            </section>
        </main>
    );
};

export default LandingPage;
